/**
 * Single source of truth för robots-direktiv.
 *
 * Använd alltid en av dessa policies istället för att skriva
 * "noindex,nofollow" eller "noindex, follow" direkt i komponenter.
 *
 * Synkad med supabase/functions/render-meta/index.ts (NOINDEX_PATH_RULES).
 */
export type RobotsPolicy =
  | 'index'           // Standard publika sidor (PDP, kategorier, brand, blogg, info)
  | 'noindex-follow'  // Filtrerade vyer, sökresultat, OOS-produkter — Google ska inte indexera men följa interna länkar
  | 'noindex-nofollow' // Privata/transaktionella sidor — varken indexera eller följa
  | 'noindex-archive'; // Diagnostik/admin — noindex + nofollow + noarchive

export const ROBOTS_CONTENT: Record<RobotsPolicy, string> = {
  'index': 'index, follow',
  'noindex-follow': 'noindex, follow',
  'noindex-nofollow': 'noindex, nofollow',
  'noindex-archive': 'noindex, nofollow, noarchive',
};

/**
 * Path-prefix-baserade defaults — använd ENBART om sidan inte
 * uttryckligen specificerar policy. Måste hållas synkad med
 * NOINDEX_PATH_RULES i render-meta/index.ts.
 */
export const PATH_POLICY_DEFAULTS: Array<{
  test: (path: string) => boolean;
  policy: RobotsPolicy;
  reason: string;
}> = [
  { test: (p) => p.startsWith('/admin'), policy: 'noindex-nofollow', reason: 'Admin' },
  { test: (p) => p === '/cart' || p === '/varukorg', policy: 'noindex-nofollow', reason: 'Cart' },
  { test: (p) => p.startsWith('/checkout') || p.startsWith('/kassa'), policy: 'noindex-nofollow', reason: 'Checkout' },
  { test: (p) => p === '/orderbekraftelse' || p === '/order-confirmation' || p.startsWith('/order/'), policy: 'noindex-nofollow', reason: 'Order confirmation' },
  { test: (p) => p === '/auth' || p === '/login' || p === '/signup' || p === '/register' || p === '/logga-in', policy: 'noindex-nofollow', reason: 'Auth' },
  { test: (p) => p === '/aterstall-losenord' || p === '/reset-password' || p === '/verifiera-email', policy: 'noindex-nofollow', reason: 'Auth flow' },
  { test: (p) => p.startsWith('/medlem') || p === '/mitt-konto', policy: 'noindex-nofollow', reason: 'Member area' },
  { test: (p) => p === '/onskerlista' || p === '/wishlist', policy: 'noindex-nofollow', reason: 'Wishlist' },
  { test: (p) => p === '/sok' || p === '/search', policy: 'noindex-follow', reason: 'Search results' },
  { test: (p) => p === '/unsubscribe' || p === '/avregistrera', policy: 'noindex-nofollow', reason: 'Unsubscribe' },
  { test: (p) => p === '/tracking-status' || p === '/domain-status', policy: 'noindex-archive', reason: 'Diagnostics' },
  { test: (p) => p === '/aterkop' || p.startsWith('/return'), policy: 'noindex-nofollow', reason: 'Return form' },
  { test: (p) => p === '/404' || p === '/not-found', policy: 'noindex-nofollow', reason: '404' },
];

export function policyForPath(path: string): RobotsPolicy {
  const match = PATH_POLICY_DEFAULTS.find((rule) => rule.test(path));
  return match?.policy ?? 'index';
}
